'use client'

import { Suspense, useEffect, useState } from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import type {
  UserGamificationSummary,
  PhishTrainerUser,
  KnowledgeProfile,
  Round,
  TierKey,
} from '@/lib/phishtrainer/types'
import { TIER_DEFS } from '@/lib/phishtrainer/types'
import { playFetch, pfetch } from '@/lib/phishtrainer/play-api'
import { toast } from 'sonner'
import { TierHeroBand } from '@/components/phishtrainer/me/tier-hero-band'
import { NextRoundCTA } from '@/components/phishtrainer/me/next-round-cta'
import { AtAGlanceCard } from '@/components/phishtrainer/me/at-a-glance-card'
import { LeaderboardCard, type LeaderboardRow } from '@/components/phishtrainer/me/leaderboard-card'
import { AchievementsRow, type AchievementItem } from '@/components/phishtrainer/me/achievements-row'
import { PillarMasteryStrip, type PillarItem } from '@/components/phishtrainer/me/pillar-mastery-strip'
import { RecentRoundsCard, type RecentRound } from '@/components/phishtrainer/me/recent-rounds-card'
import { FocusAndQuestsCard } from '@/components/phishtrainer/me/focus-and-quests-card'
import { TierUpCelebration } from '@/components/phishtrainer/me/tier-up-celebration'
import { StreakAtRiskBanner } from '@/components/phishtrainer/me/streak-at-risk-banner'
import { WeeklyQuestsCard } from '@/components/phishtrainer/me/weekly-quests-card'
import { ModePreferenceCard } from '@/components/phishtrainer/me/mode-preference-card'
import { AnonMerge } from '@/components/phishtrainer/me/anon-merge'
import { usePlayerAuth } from './_auth/player-auth-provider'

const PILLAR_LABELS: Record<string, { letter: string; label: string; color: string }> = {
  PF1: { letter: 'P', label: 'Pressure',      color: '#ef4444' },
  PF2: { letter: 'H', label: 'How-strange',   color: '#f59e0b' },
  PF3: { letter: 'I', label: 'Is-it-normal',  color: '#a855f7' },
  PF4: { letter: 'S', label: 'Sender',        color: '#3b82f6' },
  PF5: { letter: 'H', label: 'Hide-secrets',  color: '#10b981' },
}

export default function PlayerHome() {
  return (
    <Suspense fallback={<DashboardSkeleton />}>
      <PlayerHomeInner />
    </Suspense>
  )
}

function PlayerHomeInner() {
  const { user, loading: authLoading } = usePlayerAuth()

  const [summary, setSummary] = useState<UserGamificationSummary | null>(null)
  const [rounds, setRounds] = useState<Round[]>([])
  const [knowledge, setKnowledge] = useState<KnowledgeProfile | null>(null)
  const [leaderboardTop, setLeaderboardTop] = useState<LeaderboardRow[]>([])
  const [leaderboardPinned, setLeaderboardPinned] = useState<LeaderboardRow | null>(null)
  const [nextRound, setNextRound] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [celebrateTier, setCelebrateTier] = useState<TierKey | null>(null)
  const [streakBannerDismissed, setStreakBannerDismissed] = useState(false)

  useEffect(() => {
    if (!user) return
    const currentUser = user
    async function load() {
      setLoading(true)
      try {
        const id = currentUser.id
        if (!id) { setLoading(false); return }

        const [gRes, hRes, kRes, lbRes, nrRes] = await Promise.all([
          pfetch(`/api/phishtrainer/users/${id}/gamification`).then((r) => r.json()),
          pfetch(`/api/phishtrainer/users/${id}/history?limit=10`).then((r) => r.json()).catch(() => ({ data: [] })),
          pfetch(`/api/phishtrainer/users/${id}/knowledge`).then((r) => r.json()).catch(() => ({ data: null })),
          // Dashboard mini-board → real BE play leaderboard (the /api/phishtrainer
          // proxy was admin-only → mock → empty). Wrap to the {data:{top,pinned}}
          // shape the downstream code already expects.
          playFetch<{ top?: LeaderboardRow[]; pinned?: LeaderboardRow | null }>('leaderboard?limit=5')
            .then((j) => ({ data: { top: j.top ?? [], pinned: j.pinned ?? null } }))
            .catch(() => ({ data: { top: [], pinned: null } })),
          pfetch(`/api/phishtrainer/rounds/next?userId=${id}`).then((r) => r.json()).catch(() => ({ data: null })),
        ])
        setSummary(gRes.data)
        setRounds(hRes.data || [])
        const kProfile = kRes?.data?.profile ?? kRes?.data ?? null
        setKnowledge(kProfile)
        setLeaderboardTop(lbRes.data?.top ?? [])
        setLeaderboardPinned(lbRes.data?.pinned ?? null)
        setNextRound(nrRes?.data ?? null)

        // Tier-up detection (localStorage-backed)
        try {
          const lastKnownTier = localStorage.getItem(`pt-me-last-tier-${id}`) as TierKey | null
          const tierOrder: TierKey[] = ['bronze', 'silver', 'gold', 'diamond', 'rockstar']
          if (lastKnownTier && tierOrder.indexOf(gRes.data.currentTier) > tierOrder.indexOf(lastKnownTier)) {
            setCelebrateTier(gRes.data.currentTier)
          }
          localStorage.setItem(`pt-me-last-tier-${id}`, gRes.data.currentTier)
        } catch {}

        // Social comparison toasts (diff leaderboard snapshots)
        try {
          const key = `pt-me-last-lb-${id}`
          const prevRaw = localStorage.getItem(key)
          const nextTop = lbRes.data?.top ?? []
          const nextMe = (lbRes.data?.pinned?.userId === id
            ? lbRes.data.pinned
            : nextTop.find((r: any) => r.userId === id)) ?? null
          if (prevRaw && nextMe && typeof nextMe.rank === 'number') {
            const prev: { top: Array<{ userId: string; name: string; rank: number | null }>; me: { rank: number | null } | null } = JSON.parse(prevRaw)
            const prevRank = prev.me?.rank
            const nextRank = nextMe.rank
            if (typeof prevRank === 'number' && prevRank > nextRank) {
              // We climbed — find who we passed
              const passed = prev.top.filter(
                (p) => p.userId !== id && typeof p.rank === 'number' && p.rank < prevRank && p.rank >= nextRank,
              )
              for (const p of passed.slice(0, 2)) {
                toast(`🎉 You just passed ${p.name}!`, { description: 'Keep the pressure on.' })
              }
            }
          }
          localStorage.setItem(
            key,
            JSON.stringify({
              top: nextTop.map((r: any) => ({ userId: r.userId, name: r.name, rank: r.rank })),
              me: nextMe ? { rank: nextMe.rank } : null,
              at: Date.now(),
            }),
          )
        } catch {}
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [user])

  if (authLoading || loading || !user || !summary) return <DashboardSkeleton />

  const fullName = user.name || user.id
  const firstName = fullName.split(' ')[0] || 'there'

  // Derive weakest pillar from knowledge (fallback to PF3)
  const weakestPillar = deriveWeakestPillar(knowledge)

  // Next-round meta
  const nr = nextRound?.attack ?? nextRound ?? null
  const playHref = nr?.id ? `/play/game?roundId=${nr.id}` : `/play/game`
  const nextRoundProps = {
    difficulty: (nextRound as any)?.difficultyBand
      ? cap((nextRound as any).difficultyBand as string)
      : nr?.tier
        ? `${nr.tier} · ${cap(nr.difficulty ?? 'medium')}`
        : 'Medium',
    expectedStars: 2,
    // The next-round response carries the BE engine's true target pillar at the
    // top level (`weaknessTargeted`); fall back to the older `telemetry.*` mock
    // shape, then to the client-derived weakest pillar so the CTA is never blank.
    pillarFocusCode:
      (nextRound as any)?.weaknessTargeted ||
      nextRound?.telemetry?.weaknessTargeted ||
      weakestPillar.code,
    durationMin: 2,
    playHref,
  }

  // Leaderboard scopes — for v1 we only have company from the API call; team/dept returned empty.
  // We show the same "company" list under all three tabs for now; team/dept scoping would need
  // additional fetches (out of scope for this task, can be added later).
  const lbRows: LeaderboardRow[] = leaderboardTop.map((r: any) => ({
    rank: r.rank,
    userId: r.userId,
    name: r.name,
    avatar: initials(r.name),
    currentStars: r.currentStars,
    isMe: r.userId === user.id,
    delta: undefined,
  }))
  if (leaderboardPinned) {
    lbRows.push({
      rank: (leaderboardPinned as any).rank,
      userId: (leaderboardPinned as any).userId,
      name: (leaderboardPinned as any).name,
      avatar: initials((leaderboardPinned as any).name),
      currentStars: (leaderboardPinned as any).currentStars,
      isMe: true,
    })
  }
  const leaderboardScopes = { team: lbRows, dept: lbRows, company: lbRows }

  // Pie/donut: reported/clicked/missed from round history
  const progress = computeProgressBuckets(rounds)

  // Achievements
  const achievementItems: AchievementItem[] = [
    ...summary.achievements.unlocked,
    ...summary.achievements.inProgress,
    ...summary.achievements.locked,
  ]
    .slice(0, 6)
    .map((a) => ({
      id: a.def.id,
      name: a.def.name,
      desc: a.def.description,
      state: a.state,
      color: a.def.colorHex,
      iconKey: a.def.iconKey as AchievementItem['iconKey'],
      progress: a.progress,
      total: a.def.target,
      unlockedAgo: a.unlockedAt ? timeAgo(a.unlockedAt) : undefined,
    }))

  // Pillar mastery strip
  const pillars: PillarItem[] = ['PF1', 'PF2', 'PF3', 'PF4', 'PF5'].map((code) => {
    const info = PILLAR_LABELS[code]
    const signal = knowledge?.signals?.[code as keyof typeof knowledge.signals] as any
    const mastery = Math.round(((signal?.ewmaAccuracy ?? 0) as number) * 100)
    return {
      code,
      letter: info.letter,
      label: info.label,
      color: info.color,
      mastery,
      spark: Array(7).fill(mastery),  // flat line for v1; history can be added later
    }
  })

  // Recent rounds — prefer email subject as the display label; fromName as subtitle
  const recentRoundsForCard: RecentRound[] = (Array.isArray(rounds) ? rounds : []).slice(0, 7).map((r: any) => {
    const rid: string = r.id ?? r.roundId ?? ''
    const completedAt = r.completedAt || r.startedAt
    const dateLabel = completedAt
      ? new Date(completedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
      : null
    const attackLabel =
      r.subject
        ? r.subject
        : r.fromName
          ? `Email from ${r.fromName}`
          : dateLabel
            ? `Round · ${dateLabel}`
            : 'Training round'
    return {
      id: rid,
      outcome: r.outcome,
      pointsEarned: r.totalPoints ?? 0,
      stars: 0,
      timeAgo: timeAgo(completedAt),
      attack: attackLabel,
      pillar: Object.keys(r.phishRollup ?? {})[0] ?? '',
    }
  })

  // TIER_DEFS → local shape expected by the hero band
  const tiers = TIER_DEFS.map((t) => ({ key: t.key, name: t.name, threshold: t.threshold }))

  return (
    <div className="space-y-5 pb-10">
      {!streakBannerDismissed &&
        summary.streak.current > 0 &&
        summary.daysSinceActivity >= 6 && (
          <StreakAtRiskBanner
            streakCurrent={summary.streak.current}
            daysUntilDecay={Math.max(0, 7 - summary.daysSinceActivity)}
            playHref={playHref}
            onDismiss={() => setStreakBannerDismissed(true)}
          />
        )}

      <TierHeroBand
        userName={fullName}
        currentStars={summary.currentStars}
        currentTier={summary.currentTier as TierKey}
        peakTier={summary.peakTier as TierKey}
        nextTier={summary.nextTier as TierKey | null}
        starsToNextTier={summary.starsToNextTier}
        progressToNextTier={summary.progressToNextTier}
        streakCurrent={summary.streak.current}
        weekStats={summary.weekStats}
        tiers={tiers}
        howItWorksHref="/play/me"
      />

      <NextRoundCTA {...nextRoundProps} />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3">
          <LeaderboardCard firstName={firstName} scopes={leaderboardScopes} />
        </div>
        <div className="lg:col-span-2">
          <AtAGlanceCard
            progress={progress}
            streak={{ current: summary.streak.current, best: summary.streak.best }}
            pillarSlam={{ done: summary.pillarSlam.doneCodes.length, total: summary.pillarSlam.total }}
          />
        </div>
      </div>

      <AchievementsRow items={achievementItems} />

      {summary.weeklyQuests && <WeeklyQuestsCard data={summary.weeklyQuests} />}

      <PillarMasteryStrip pillars={pillars} />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2">
          <FocusAndQuestsCard
            weakestPillar={weakestPillar}
            pillarSlam={{ doneCodes: summary.pillarSlam.doneCodes, total: summary.pillarSlam.total }}
            streakCurrent={summary.streak.current}
            trainHref={playHref}
          />
        </div>
        <div className="lg:col-span-3">
          <RecentRoundsCard rounds={recentRoundsForCard} />
        </div>
      </div>

      <AnonMerge userId={user.id} />
      <ModePreferenceCard userId={user.id} />

      {celebrateTier && (
        <TierUpCelebration newTier={celebrateTier} onDismiss={() => setCelebrateTier(null)} />
      )}
    </div>
  )
}

function DashboardSkeleton() {
  return (
    <div className="space-y-5 pb-10">
      <Skeleton className="h-[400px] w-full rounded-2xl" />
      <Skeleton className="h-24 w-full rounded-2xl" />
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <Skeleton className="h-72 lg:col-span-3 rounded-2xl" />
        <Skeleton className="h-72 lg:col-span-2 rounded-2xl" />
      </div>
      <Skeleton className="h-40 rounded-2xl" />
      <Skeleton className="h-44 rounded-2xl" />
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <Skeleton className="h-60 lg:col-span-2 rounded-2xl" />
        <Skeleton className="h-60 lg:col-span-3 rounded-2xl" />
      </div>
    </div>
  )
}

function cap(s: string) { return s.charAt(0).toUpperCase() + s.slice(1) }

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('')
}

function deriveWeakestPillar(knowledge: KnowledgeProfile | null): { code: string; label: string; mastery: number } {
  const fallback = { code: 'PF3', label: 'Is-it-normal', mastery: 0 }
  if (!knowledge?.signals) return fallback
  const signals = knowledge.signals as Record<string, { ewmaAccuracy?: number }>
  let worst = { code: '', label: '', mastery: 101 }
  for (const code of ['PF1', 'PF2', 'PF3', 'PF4', 'PF5']) {
    const acc = Math.round((signals[code]?.ewmaAccuracy ?? 0) * 100)
    if (acc < worst.mastery) worst = { code, label: PILLAR_LABELS[code]?.label ?? '', mastery: acc }
  }
  return worst.code ? worst : fallback
}

function computeProgressBuckets(rounds: Round[]): { reported: number; clicked: number; missed: number } {
  if (!rounds.length) return { reported: 0, clicked: 0, missed: 0 }
  let reported = 0, clicked = 0, missed = 0
  for (const r of rounds) {
    if (r.outcome === 'passed') reported++
    else if (r.outcome === 'failed') missed++
    else clicked++
  }
  return { reported, clicked, missed }
}

function timeAgo(iso?: string): string {
  if (!iso) return ''
  const ms = Date.now() - Date.parse(iso)
  if (Number.isNaN(ms) || ms < 0) return ''
  const min = Math.round(ms / 60_000)
  if (min < 60) return `${min}m ago`
  const h = Math.round(min / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.round(h / 24)
  return `${d}d ago`
}
